import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ContentPageTheme, Player, ReviewPageConfig, ReviewPageConfigServices } from "@/services/bedrock";
import { publicLinkOrigin, publicWebOrigin, useDebounced } from "@/utils";
import { findTheme } from "../../components/sampleReviews";
import { useIframePreview } from "../../components/useIframePreview";
import { PLAYER_ID_PLACEHOLDER, usePlayerPreviewLoading } from "../../components/usePreviewPlayer";
import { SHOW_ALL_SECTIONS, applyDisplaySettings, buildFullReviewsPagePreviewData } from "./fullReviewsPagePreviewData";
import { getFullReviewsPageSettings, fullReviewsPagePreviewParams } from "./fullReviewsPageSettings";

const TEXT_DEBOUNCE_MS = 400;

interface Args {
  config: ReviewPageConfig;
  themes: ContentPageTheme[];
  previewPlayer: Player | null;
}

const useFullReviewsPagePreview = ({ config, themes, previewPlayer }: Args) => {
  const origin = publicWebOrigin();
  const iframeSrc = `${origin}/r/preview`;
  const playerGuid = previewPlayer?.guid;
  const settings = useMemo(() => getFullReviewsPageSettings(config), [config]);
  const headline = useDebounced(settings.headline, TEXT_DEBOUNCE_MS);
  const subtitle = useDebounced(settings.subtitle, TEXT_DEBOUNCE_MS);
  const previewParams = useMemo(() => {
    const params = fullReviewsPagePreviewParams({ ...settings, ...SHOW_ALL_SECTIONS, headline, subtitle });
    params.set("sentiment", "all");
    return params.toString();
  }, [settings, headline, subtitle]);

  const {
    data: playerData,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["reviewPagePublicPreview", playerGuid, previewParams],
    queryFn: () => ReviewPageConfigServices.getPreview(playerGuid!, new URLSearchParams(previewParams)),
    enabled: !!playerGuid,
    placeholderData: previousData => previousData,
  });

  const isLoading = usePlayerPreviewLoading({ playerGuid, isFetching, isError, error });

  const samplePayload = useMemo(
    () => (playerGuid ? null : buildFullReviewsPagePreviewData(settings, themes)),
    [playerGuid, settings, themes]
  );
  const { contentPageThemeId } = settings;

  const message = useMemo(() => {
    if (samplePayload) return { type: "REVIEW_PAGE", payload: samplePayload };
    if (isError || !playerData) return { type: "REVIEW_PAGE", payload: null };

    const selectedTheme = findTheme(themes, contentPageThemeId);
    return {
      type: "REVIEW_PAGE",
      payload: { ...applyDisplaySettings(playerData, settings), ...(selectedTheme ? { theme: selectedTheme } : {}) },
    };
  }, [samplePayload, themes, contentPageThemeId, playerData, isError, settings]);

  const { iframeRef } = useIframePreview({ src: iframeSrc, message });

  const playerSegment = playerGuid ? encodeURIComponent(playerGuid) : PLAYER_ID_PLACEHOLDER;
  const pageUrl = `${publicLinkOrigin()}/r/${playerSegment}`;

  return {
    iframeRef,
    iframeSrc,
    pageUrl,
    isSamplePreview: !playerGuid,
    isLoading,
    previewPlayerName: previewPlayer?.fullName || undefined,
  };
};

export { useFullReviewsPagePreview };
